import { createSlice } from "@reduxjs/toolkit";
import find from "lodash/find";
import { RECEIVE_CREATE_COMPANY, RECEIVE_UPDATE_COMPANY } from "@/redux/constants/companies";

const addCacheBuster = url => (url ? `${url}?v=${Date.now()}` : url);

const initialState = {
  userConnected: null,
  currentCompany: {},
  darkMode: null,
  selectedCompany: null,
  isFetching: false,
  isSaving: false,
  isRefreshing: false,
};

const currentUserSlice = createSlice({
  name: "currentUser",
  initialState,
  reducers: {
    setCurrentUser(state, { payload }) {
      const { account, company } = payload;
      state.userConnected = { ...account, pictureUrl: addCacheBuster(account.pictureUrl) };
      state.currentCompany = { ...company, logoUrl: addCacheBuster(company.logoUrl) };
      state.darkMode = account.darkMode;
      state.isFetching = false;
    },
    setCompanyLoanCount(state, { payload }) {
      state.currentCompany.loanCount = payload;
    },
    setUpdatedUser(state, { payload }) {
      const account = payload.account;
      state.userConnected = { ...account, pictureUrl: addCacheBuster(account.pictureUrl) };
      state.darkMode = account.darkMode;
      state.isSaving = false;
    },
    setDarkMode(state, { payload }) {
      if (state.userConnected) {
        state.userConnected.darkMode = payload;
      }
      state.darkMode = payload;
      state.isRefreshing = false;
    },
    setSelectedCompany(state, { payload: companyId }) {
      state.selectedCompany = find(state.userConnected?.companies, ["companyId", companyId]) || null;
    },
    setAvatarUpdated(state, { payload: account }) {
      if (state.userConnected) {
        state.userConnected.pictureUrl = addCacheBuster(account.pictureUrl);
      }
      state.isSaving = false;
    },
    setCompanyLogoUpdated(state, { payload: company }) {
      state.currentCompany = { ...state.currentCompany, logoUrl: addCacheBuster(company.logoUrl) };
      state.isSaving = false;
    },
    setCompanyUpdated(state, { payload: company }) {
      const employeeId = state.currentCompany.employeeId;
      state.currentCompany = { ...company, employeeId };
      if (state.userConnected) {
        state.userConnected.company = { ...company, employeeId };
      }
      state.isSaving = false;
    },
    setFetching(state, { payload }) {
      state.isFetching = payload;
    },
    setSaving(state, { payload }) {
      state.isSaving = payload;
    },
    setRefreshing(state, { payload }) {
      state.isRefreshing = payload;
    },
    resetLoadingStates(state) {
      state.isFetching = false;
      state.isSaving = false;
      state.isRefreshing = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(RECEIVE_CREATE_COMPANY, (state, action) => {
        state.currentCompany = action.company;
        if (state.userConnected) {
          state.userConnected.company = action.company;
          state.userConnected.employeeId = action.company.employeeId;
        }
        state.isSaving = false;
      })
      .addCase(RECEIVE_UPDATE_COMPANY, (state, action) => {
        const employeeId = state.currentCompany.employeeId;
        state.currentCompany = { ...action.company, employeeId };
        if (state.userConnected) {
          state.userConnected.company = { ...action.company, employeeId };
        }
        state.isSaving = false;
      });
  },
});

export const {
  setCurrentUser,
  setCompanyLoanCount,
  setUpdatedUser,
  setDarkMode,
  setSelectedCompany,
  setAvatarUpdated,
  setCompanyLogoUpdated,
  setCompanyUpdated,
  setFetching,
  setSaving,
  setRefreshing,
  resetLoadingStates,
} = currentUserSlice.actions;

export const currentUserReducer = currentUserSlice.reducer;

